import { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@itemgo/core";

export interface AdminStats {
  totalUsers: number;
  totalDrivers: number;
  totalCustomers: number;
  totalBusinesses: number;
  activeBookings: number;
  completedBookings: number;
  revenueMtd: number;
  pendingPayouts: number;
  totalJobs: number;
  openJobs: number;
}

interface AdminDataState {
  stats: AdminStats;
  loading: boolean;
}

const AdminDataContext = createContext<AdminDataState | null>(null);

export function AdminDataProvider({ children }: { children: React.ReactNode }) {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalDrivers: 0,
    totalCustomers: 0,
    totalBusinesses: 0,
    activeBookings: 0,
    completedBookings: 0,
    revenueMtd: 0,
    pendingPayouts: 0,
    totalJobs: 0,
    openJobs: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const usersUnsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const users = snap.docs.map((d) => d.data());
        setStats((prev) => ({
          ...prev,
          totalUsers: users.length,
          totalDrivers: users.filter((u: any) => u.role === "driver").length,
          totalCustomers: users.filter((u: any) => u.role === "customer").length,
          totalBusinesses: users.filter((u: any) => u.role === "business").length,
        }));
      },
      (err) => console.warn("[AdminData] users snapshot error:", err)
    );

    const activeUnsub = onSnapshot(
      query(collection(db, "bookings"), where("status", "in", ["pending", "confirmed", "driver_assigned", "in_progress"])),
      (snap) => setStats((prev) => ({ ...prev, activeBookings: snap.size })),
      (err) => console.warn("[AdminData] active bookings error:", err)
    );

    const completedUnsub = onSnapshot(
      query(collection(db, "bookings"), where("status", "==", "completed")),
      (snap) => setStats((prev) => ({ ...prev, completedBookings: snap.size })),
      (err) => console.warn("[AdminData] completed bookings error:", err)
    );

    // Revenue: use a simpler query without composite index
    const revenueUnsub = onSnapshot(
      query(collection(db, "bookings"), where("status", "==", "completed")),
      (snap) => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const revenue = snap.docs.reduce((sum, d) => {
          const data = d.data();
          const completedAt = data.completedAt?.toDate?.()?.getTime() ?? 0;
          if (completedAt < monthStart) return sum;
          const pricing = data.pricing || {};
          return sum + (pricing.customerTotal || 0);
        }, 0);
        setStats((prev) => ({ ...prev, revenueMtd: revenue }));
      },
      (err) => console.warn("[AdminData] revenue error:", err)
    );

    const payoutsUnsub = onSnapshot(
      query(collection(db, "scheduledPayouts"), where("status", "==", "pending")),
      (snap) => setStats((prev) => ({ ...prev, pendingPayouts: snap.size })),
      (err) => console.warn("[AdminData] payouts error:", err)
    );

    const jobsUnsub = onSnapshot(
      collection(db, "jobPostings"),
      (snap) => {
        const jobs = snap.docs.map((d) => d.data());
        setStats((prev) => ({
          ...prev,
          totalJobs: jobs.length,
          openJobs: jobs.filter((j: any) => j.status === "open").length,
        }));
      },
      (err) => console.warn("[AdminData] jobs error:", err)
    );

    const timer = setTimeout(() => setLoading(false), 500);

    return () => {
      usersUnsub();
      activeUnsub();
      completedUnsub();
      revenueUnsub();
      payoutsUnsub();
      jobsUnsub();
      clearTimeout(timer);
    };
  }, []);

  return (
    <AdminDataContext.Provider value={{ stats, loading }}>
      {children}
    </AdminDataContext.Provider>
  );
}

export function useAdminData() {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error("useAdminData must be used within AdminDataProvider");
  return ctx;
}
